import { formatOfferPrice } from './productOffers';

export const DEFAULT_HOTPOINT_PRICE_MATCH = {
  enabled: false,
  minConfidence: 80,
  minSavingsKes: 500,
  priceSnapshotMaxAgeHours: 24,
  guaranteeEndDate: null,
  competitorStoreAllowlist: [],
  pilotCategories: [],
};

export function normalizeHotpointPriceMatch(raw) {
  const h = raw && typeof raw === 'object' ? raw : {};
  return {
    ...DEFAULT_HOTPOINT_PRICE_MATCH,
    enabled: h.enabled === true,
    minConfidence: Number.isFinite(Number(h.minConfidence)) ? Number(h.minConfidence) : 80,
    minSavingsKes: Number.isFinite(Number(h.minSavingsKes)) ? Number(h.minSavingsKes) : 500,
    priceSnapshotMaxAgeHours: Number.isFinite(Number(h.priceSnapshotMaxAgeHours))
      ? Number(h.priceSnapshotMaxAgeHours)
      : 24,
    guaranteeEndDate: h.guaranteeEndDate || null,
    competitorStoreAllowlist: Array.isArray(h.competitorStoreAllowlist)
      ? h.competitorStoreAllowlist
      : DEFAULT_HOTPOINT_PRICE_MATCH.competitorStoreAllowlist,
    pilotCategories: Array.isArray(h.pilotCategories) ? h.pilotCategories : [],
  };
}

export function isPriceMatchVisible(priceMatch, settingsEnabled) {
  return Boolean(settingsEnabled && priceMatch?.enabled && priceMatch?.savingsAmount > 0);
}

export function isHotpointProduct(product) {
  const name = String(
    product?.storeName || product?.vendor?.name || product?.store?.name || ''
  ).toLowerCase();
  return name.includes('hotpoint');
}

export function productHasVisiblePriceMatch(product, showPriceMatch) {
  return isPriceMatchVisible(product?.priceMatch, showPriceMatch);
}

export function countVisiblePriceMatchProducts(products, showPriceMatch) {
  if (!showPriceMatch || !Array.isArray(products)) return 0;
  return products.reduce(
    (total, product) => total + (productHasVisiblePriceMatch(product, showPriceMatch) ? 1 : 0),
    0
  );
}

export function formatPriceMatchSavings(amount, currency = 'KES') {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (currency === 'KES') {
    return `Save ${formatOfferPrice(value)}`;
  }
  return `Save ${currency} ${value.toLocaleString()}`;
}

export function formatPriceMatchShortStore(storeName) {
  const name = String(storeName || '').trim();
  if (!name) return 'competitor';
  return name.replace(/\s+kenya$/i, '').trim() || name;
}

export function formatProofCapturedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-KE', {
    timeZone: 'Africa/Nairobi',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function resolveHotpointClaimUrl(product, priceMatch) {
  const vendorUrl = String(product?.vendorProductUrl || '').trim();
  if (vendorUrl && /^https?:\/\//i.test(vendorUrl)) return vendorUrl;
  return 'https://hotpoint.co.ke/price-match/';
}

export function buildPriceMatchProofText({ product, priceMatch, priceMatchOthers = [] }) {
  if (!priceMatch) return '';
  const lines = [
    `Hotpoint Price Match proof — ${product?.name || 'Product'}`,
    `Hotpoint: ${formatOfferPrice(priceMatch.hotpointPrice)}`,
    `${formatPriceMatchShortStore(priceMatch.competitorStoreName)}: ${formatOfferPrice(priceMatch.competitorPrice)}`,
    `Potential saving: ${formatPriceMatchSavings(priceMatch.savingsAmount, priceMatch.currency)}`,
  ];
  if (priceMatch.competitorProofUrl) {
    lines.push(`Competitor listing: ${priceMatch.competitorProofUrl}`);
  }
  const captured = formatProofCapturedAt(priceMatch.priceCapturedAt);
  if (captured) lines.push(`Proof captured on Raheasy: ${captured} EAT`);
  if (priceMatchOthers.length) {
    lines.push('', 'Also cheaper at:');
    priceMatchOthers.forEach((match) => {
      lines.push(
        `- ${formatPriceMatchShortStore(match.competitorStoreName)}: ${formatOfferPrice(match.competitorPrice)}${match.competitorProofUrl ? ` (${match.competitorProofUrl})` : ''}`
      );
    });
  }
  lines.push('', 'Claim on Hotpoint if you qualify: https://hotpoint.co.ke/price-match-terms/');
  return lines.join('\n');
}

export async function copyPriceMatchProof(payload) {
  const text = buildPriceMatchProofText(payload);
  if (!text) return { ok: false, reason: 'empty' };
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return { ok: true };
    }
  } catch {
    /* fall through */
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return { ok };
  } catch {
    return { ok: false, reason: 'clipboard_unavailable' };
  }
}
